/**
 * PageHeroCompact — Slim gradient strip header for lighter pages
 * (forms, lists, settings). Same visual vocabulary as PageHero so
 * the section-color cue carries through, just with much less
 * vertical real estate.
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { isWarmupFrame } from '../../lib/pageCache';
import logger from '../../lib/logger';
import { useRegisterPageTitle } from '../../context/PageTitleContext';

export function PageHeroCompact({
  title,
  description,
  theme,
  icon: Icon,
  actions,
  testId = 'page-hero-compact',
}) {
  const location = useLocation();
  // Publish the live title so the Header breadcrumb mirrors this hero.
  useRegisterPageTitle(title);
  // Fire a fire-and-forget page-view beacon on mount. Used by the
  // admin Page Funnel tile to surface which sections users actually
  // visit each week (free pulse-check during the UX migration).
  // Failures are swallowed silently — telemetry must never block
  // the user.
  useEffect(() => {
    if (!title || isWarmupFrame()) return;
    api
      .post('/analytics/page-view/track', {
        path: location.pathname || '/',
        title: String(title),
        theme: theme?.name || null,
      })
      .catch((err) => logger.debug?.('page-view track failed', err));
  }, [title, theme?.name, location.pathname]);

  return (
    <div
      className={cn(
        'relative w-full text-white shadow-sm',
        theme.heroGradient,
      )}
      data-testid={testId}
    >
      <div className="px-4 md:px-8 lg:px-12 py-4 md:py-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              {Icon && (
                <Icon
                  className={cn('h-5 w-5 md:h-6 md:w-6 shrink-0', theme.titleClass)}
                  data-testid={`${testId}-icon`}
                />
              )}
              <h1
                className={cn(
                  'text-xl md:text-2xl font-bold tracking-tight',
                  theme.titleClass,
                )}
                data-testid={`${testId}-title`}
              >
                {title}
              </h1>
            </div>
            {description && (
              <p
                className={cn(
                  'mt-1 max-w-3xl text-xs md:text-sm',
                  theme.textLighter,
                )}
                data-testid={`${testId}-description`}
              >
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div
              className="flex flex-wrap items-center gap-2 shrink-0"
              data-testid={`${testId}-actions`}
            >
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageHeroCompact;
